/**
 * Breadcrumb for the ManagePlatform page.
 *
 * Renders up to 5 levels:
 *   1. لوحة التحكم  (always)
 *   2. CLOUDGM FZ LLC  (always)
 *   3. منصة العملاء/التجار (Supervision)  (always)
 *   4. parent hub (الوكلاء / إدارة المعروضات)  (when inside a sub-tab)
 *   5. current tab + optional sub-page (5th gold-underlined)
 *
 * Extracted from ManagePlatform.js (iter-78) to keep that file lean.
 */
import { useNavigate } from 'react-router-dom';

// Which parent-hub a sub-tab belongs to (used to render an intermediate
// breadcrumb link AND to wire the back button).
const PARENT_HUB = {
  'agent-requests': 'agents-management',
  'agents': 'agents-management',
  'invitation-codes': 'agents-management',
  'agent-tasks': 'agents-management',
  'add-agent-task': 'agents-management',
  'follow-agent-tasks': 'agents-management',
  'listings-cars': 'listings-management',
  'listings-car-plates': 'listings-management',
  'listings-numbers': 'listings-management',
  'listings-real-estate': 'listings-management',
  'listings-ecommerce': 'listings-management',
  'market-phone-numbers': 'market-follow',
  'market-cars': 'market-follow',
  'market-car-plates': 'market-follow',
};

const HUB_LABELS = {
  'agents-management': { ar: 'الوكلاء', en: 'Agents' },
  'listings-management': { ar: 'إدارة المعروضات', en: 'Listings Management' },
  'market-follow': { ar: 'متابعة السوق', en: 'Market Follow' },
};

// Bilingual labels for every tab/sub-tab that can be active.
const TAB_LABELS = {
  'users': { ar: 'العملاء', en: 'Customers' },
  'cloudgm-users': { ar: 'عملاء كلاود جي ام', en: 'CloudGM Customers' },
  'accounts': { ar: 'الحسابات', en: 'Accounts' },
  'support-tickets': { ar: 'تذاكر الدعم', en: 'Support Tickets' },
  'services-categories': { ar: 'طلبات الخدمات', en: 'Service Requests' },
  'market-follow': { ar: 'متابعة السوق', en: 'Market Follow' },
  'market-phone-numbers': { ar: 'أرقام الهواتف', en: 'Phone Numbers' },
  'market-cars': { ar: 'السيارات', en: 'Cars' },
  'market-car-plates': { ar: 'أرقام السيارات', en: 'Car Plates' },
  'store-requests': { ar: 'طلبات تفعيل المتاجر', en: 'Store Activation Requests' },
  'subscriptions': { ar: 'الاشتراكات', en: 'Subscriptions' },
  'stores': { ar: 'إدارة المتاجر', en: 'Stores Management' },
  'stores-management': { ar: 'إدارة المتاجر', en: 'Stores Management' },
  'agent-requests': { ar: 'طلبات عضوية وكيل', en: 'Agent Requests' },
  'agents': { ar: 'وكلاء الشركة', en: 'Company Agents' },
  'agents-management': { ar: 'الوكلاء', en: 'Agents' },
  'invitation-codes': { ar: 'دعوات الوكلاء', en: 'Agent Invites' },
  'agent-tasks': { ar: 'مهام الوكلاء', en: 'Agent Tasks' },
  'add-agent-task': { ar: 'إدراج مهام للوكلاء', en: 'Create Agent Tasks' },
  'follow-agent-tasks': { ar: 'متابعة مهام الوكلاء', en: 'Track Agent Tasks' },
  'listings-management': { ar: 'إدارة المعروضات', en: 'Listings Management' },
  'listings-cars': { ar: 'السيارات', en: 'Cars' },
  'listings-car-plates': { ar: 'لوحات السيارات', en: 'Car Plates' },
  'listings-numbers': { ar: 'أرقام الموبايل والهواتف', en: 'Phone & Mobile Numbers' },
  'listings-real-estate': { ar: 'العقارات', en: 'Real Estate' },
  'listings-ecommerce': { ar: 'المتاجر الإلكترونية', en: 'E-Commerce Stores' },
  'order-tracking': { ar: 'تتبّع الطلبات', en: 'Order Tracking' },
};

const linkCls = 'text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer';
const activeCls = 'text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5';

export function getParentHub(activeTab) {
  return PARENT_HUB[activeTab] || null;
}

export default function PlatformBreadcrumb({
  activeTab,
  setActiveTab,
  isCloudGM,
  language,
  storeBusinessType,
  setStoreBusinessType,
  storeManageType,
  setStoreManageType,
}) {
  const navigate = useNavigate();
  const parentHub = PARENT_HUB[activeTab] || null;
  const parentHubLabel = parentHub ? HUB_LABELS[parentHub] : null;

  // Detect 5th-level sub-page (depends on inner chooser state)
  let subLabel = null;
  if (activeTab === 'store-requests' && storeBusinessType === 'personal') {
    subLabel = { ar: 'تفعيل المتاجر الشخصية', en: 'Personal Stores Activation' };
  } else if (activeTab === 'store-requests' && storeBusinessType === 'company') {
    subLabel = { ar: 'تفعيل متاجر الشركات', en: 'Company Stores Activation' };
  } else if (activeTab === 'stores' && storeManageType === 'personal') {
    subLabel = { ar: 'إدارة متاجر شخصية', en: 'Personal Stores Management' };
  } else if (activeTab === 'stores' && storeManageType === 'company') {
    subLabel = { ar: 'إدارة متاجر الشركات', en: 'Company Stores Management' };
  }

  const tabLabel = TAB_LABELS[activeTab] || { ar: activeTab, en: activeTab };
  const platformLabelAr = isCloudGM ? 'منصة العملاء (الإشراف)' : 'منصة التجار (الإشراف)';
  const platformLabelEn = isCloudGM ? 'Customers Platform (Supervision)' : 'Merchants Platform (Supervision)';
  const platformLabel = language === 'ar' ? platformLabelAr : platformLabelEn;

  return (
    <div className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {activeTab !== 'main' ? (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <button onClick={() => navigate('/dashboard')} className={linkCls}>
              {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </button>
            <span className="text-[#71717A]">{'<'}</span>
            <button onClick={() => navigate('/dashboard?platform=fzlc')} className={linkCls}>
              CLOUDGM FZ LLC
            </button>
            <span className="text-[#71717A]">{'<'}</span>
            <button onClick={() => setActiveTab('main')} className={linkCls}>
              {platformLabel}
            </button>

            {parentHubLabel && (
              <>
                <span className="text-[#71717A]">{'<'}</span>
                <button
                  onClick={() => setActiveTab(parentHub)}
                  className={linkCls}
                  data-testid={`breadcrumb-${parentHub}`}
                >
                  {language === 'ar' ? parentHubLabel.ar : parentHubLabel.en}
                </button>
              </>
            )}

            {subLabel ? (
              <>
                <span className="text-[#71717A]">{'<'}</span>
                <button
                  onClick={() => {
                    if (activeTab === 'store-requests') setStoreBusinessType(null);
                    if (activeTab === 'stores') setStoreManageType(null);
                  }}
                  className={linkCls}
                  data-testid={`breadcrumb-${activeTab}`}
                >
                  {language === 'ar' ? tabLabel.ar : tabLabel.en}
                </button>
                <span className="text-[#71717A]">{'<'}</span>
                <span className={activeCls} data-testid="breadcrumb-current-sub">
                  {language === 'ar' ? subLabel.ar : subLabel.en}
                </span>
              </>
            ) : (
              <>
                <span className="text-[#71717A]">{'<'}</span>
                <span className={activeCls}>
                  {language === 'ar' ? tabLabel.ar : tabLabel.en}
                </span>
              </>
            )}
          </div>
        ) : (
          // Top-level 'main' tab — 3-level only
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate('/dashboard')} className={linkCls}>
              {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </button>
            <span className="text-[#71717A]">{'<'}</span>
            <button onClick={() => navigate('/dashboard?platform=fzlc')} className={linkCls}>
              CLOUDGM FZ LLC
            </button>
            <span className="text-[#71717A]">{'<'}</span>
            <span className={activeCls}>{platformLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}
